import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { HomeTabs, Search } from '@local/client-contracts';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { WorkspacesService } from 'src/app/bar/services';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchService } from 'src/app/bar/services/search';
import { MailSourceSettings } from 'src/app/bar/services/search/client';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { ResultItem } from '../../../results/models/results-types';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';

@UntilDestroy()
@Component({
  selector: 'mail-widget',
  templateUrl: './mail-widget.component.html',
  styleUrls: ['./mail-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailWidgetComponent extends WidgetBaseComponent<HomeTabs.Widget> implements OnInit, OnDestroy {
  private isPaging = false;
  private lastPageInfo: { startIndex: number; endIndex: number; last: boolean };

  constructor(
    protected elementRef: ElementRef,
    protected eventsService: EventsService,
    protected hubService: HubService,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected resultCommandService: ResultCommandService,
    protected ngZone: NgZone,
    private searchService: SearchService,
    protected session: SessionService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected routerService: RouterService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
    this.searchSession = this.searchService.getOrCreateSearchSession('mailWidget');
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.pageType = 'mail';
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.searchSession.destroy();
  }

  loadData() {
    this.isLoading = true;

    const options: SearchOptions = {
      resetSession: true,
      trigger: 'email_widget_load',

      sources: [
        {
          filters: {},
          id: 'working-on-mail',
          type: 'mail-resources',
          requestMaxCount: 10,
          useSourceFilters: true,
          caching: { strategy: 'cache-and-source' },
          sorting: {
            by: 'Timestamp',
            order: 'Descending',
            type: 'Time',
          },
          noHeader: true,
          ignoreDateHeaders: true,
        } as MailSourceSettings,
      ],
    };

    this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx) => {
        if (!this.isPaging) {
          let items = ctx.items;
          // remove duplicated items
          const itemIds = ctx.items.map((o) => (o as ResultItem).id);
          items = items.filter((i: ResultItem, index) => !itemIds.includes(i.id, index + 1));

          ctx.items = items;
          this.displayedContext = ctx;
          this.lastPageInfo = {
            endIndex: items.length - 1,
            startIndex: items.length - 3,
            last: true,
          };
          this.isLoading = !ctx.searchCompleted && !items.length;
          this.cdr.markForCheck();
        }
      });
  }

  public async onInvoke(data: InvokeCommand, type: Search.ResultType): Promise<void> {
    const eventInfo = { ...data.eventInfo, resources: [{ list: 'actions' }] };
    data = { ...data, eventInfo };
    this.resultCommandService.onInvoke(
      this.displayedContext.items,
      data,
      type,
      'favorites',
      'favorites',
      'favorites',
      this.selectedIndex,
      0
    );
  }
}
